import { useEffect, useState } from 'react'
import { Alert, Col, Container, Form, Nav, Row, Tab, Table } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import KLayerNavigation from '../components/KLayerNavigation'
import KButton from '../components/KButton'
import { isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'

interface ConnectionStatus {
  configured: boolean
  connected: boolean
  expired: boolean
  expiresAt: string | null
  authType?: 'oauth' | 'pat'
}

interface BexioContact {
  id: number
  contact_type_id: number
  name_1: string
  name_2: string | null
  address: string | null
  birthday: string | null
}

export default function BexioSettings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState<ConnectionStatus | null>(null)
  const [patToken, setPatToken] = useState('')

  const [contacts, setContacts] = useState<BexioContact[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ created: number; updated: number } | null>(
    null
  )

  useEffect(() => {
    fetchStatus()

    const bexioParam = searchParams.get('bexio')
    if (bexioParam === 'connected') {
      showKAlert('success', t('bexioConnected'))
      setSearchParams({})
    } else if (bexioParam === 'error') {
      const reason = searchParams.get('reason') ?? ''
      showKAlert('danger', `${t('bexioConnectionFailed')}: ${reason}`)
      setSearchParams({})
    }
  }, [])

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/connection/status`)
      const data = await res.json()
      setStatus(data)
    } catch {
      setStatus(null)
    }
  }

  const formatExpiresAt = (isoDate: string) =>
    new Date(isoDate).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const refreshToken = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/refresh`, { method: 'POST' })
      const data = await res.json()
      if (data.ok) fetchStatus()
    } catch {
      showKAlert('danger', t('bexioRefreshFailed'))
    }
  }

  const savePat = async () => {
    if (!patToken.trim()) {
      showKAlert('danger', t('bexioPatRequired'))
      return
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/pat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: patToken.trim() }),
      })
      const data = await res.json()
      if (data.ok) {
        setPatToken('')
        fetchStatus()
        showKAlert('success', t('bexioPatConnected'))
      }
    } catch {
      showKAlert('danger', t('bexioPatSaveFailed'))
    }
  }

  const disconnect = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/connection`, {
        method: 'DELETE',
      })
      const data = await res.json()
      if (data.ok) {
        setStatus({ configured: false, connected: false, expired: false, expiresAt: null })
        setContacts(null)
        setSelectedIds(new Set())
      }
    } catch {
      showKAlert('danger', t('bexioDisconnectFailed'))
    }
  }

  const loadContacts = async () => {
    setLoadingContacts(true)
    setContacts(null)
    setSelectedIds(new Set())
    setImportResult(null)
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/import/contacts`)
      const data = await res.json()
      if (data.ok) {
        setContacts(data.contacts)
      }
    } catch {
      showKAlert('danger', t('bexioLoadContactsFailed'))
    } finally {
      setLoadingContacts(false)
    }
  }

  const toggleContact = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (!contacts) return
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)))
    }
  }

  const importSelected = async () => {
    if (!contacts || selectedIds.size === 0) return
    setImporting(true)
    setImportResult(null)
    try {
      const selected = contacts.filter(c => selectedIds.has(c.id))
      const res = await fetch(`${process.env.REACT_APP_API_URL}/bexio/import/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: selected }),
      })
      const data = await res.json()
      if (data.ok) {
        setImportResult({ created: data.created, updated: data.updated })
        setSelectedIds(new Set())
      }
    } catch {
      showKAlert('danger', t('bexioImportFailed'))
    } finally {
      setImporting(false)
    }
  }

  const allSelected = !!contacts && contacts.length > 0 && selectedIds.size === contacts.length

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='ms-sm-auto col-lg-10 px-md-4'>
          <div className='col-12'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {t('bexioIntegration')}
            </p>
          </div>

          <div className='col-12 my-5'>
            {status?.connected && (
              <Row className='mb-4'>
                <Col lg={{ span: 8, offset: 4 }}>
                  <Alert
                    variant={status.expired ? 'danger' : 'info'}
                    className='mb-0 d-flex justify-content-between align-items-center'
                  >
                    <div>
                      {status.expired ? (
                        <>
                          <i className='bi bi-exclamation-triangle-fill me-2'></i>
                          {t('bexioTokenExpired')}
                        </>
                      ) : (
                        <>
                          <i className='bi bi-check-circle-fill me-2'></i>
                          {t('bexioTokenActive')}
                          {status.expiresAt && (
                            <>
                              {' '}
                              — {t('bexioTokenExpiresAt')}{' '}
                              <strong>{formatExpiresAt(status.expiresAt)}</strong>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <div className='d-flex gap-2'>
                      {status.expired && (
                        <KButton
                          id='btn-bexio-refresh'
                          label={t('bexioRefreshToken')}
                          variant='outline-primary'
                          icon='arrow-repeat'
                          size='sm'
                          onClick={refreshToken}
                        />
                      )}
                      <KButton
                        id='btn-bexio-disconnect'
                        label={t('bexioDisconnect')}
                        variant='outline-danger'
                        icon='x-circle'
                        size='sm'
                        onClick={disconnect}
                      />
                    </div>
                  </Alert>
                </Col>
              </Row>
            )}

            <Tab.Container defaultActiveKey='pat'>
              <Row className='mb-4'>
                <Col lg={{ span: 8, offset: 4 }}>
                  <Nav variant='tabs'>
                    <Nav.Item>
                      <Nav.Link eventKey='pat'>{t('bexioTabPat')}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>

              <Tab.Content>
                <Tab.Pane eventKey='pat'>
                  <Row className='mb-5'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <p className='mt-1 text-draft'>{t('bexioPatSetup')}</p>
                        </div>
                      </div>
                    </Col>
                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        <p className='text-muted mb-3'>{t('bexioPatDescription')}</p>
                        <Form>
                          <Form.Group className='mb-3'>
                            <Form.Label className='fw-medium smaller text-primary'>
                              {t('bexioPatLabel')}
                            </Form.Label>
                            <Form.Control
                              type='password'
                              placeholder={t('bexioPatPlaceholder')}
                              value={patToken}
                              onChange={e => setPatToken(e.target.value)}
                            />
                          </Form.Group>
                          <KButton
                            id='btn-bexio-pat-save'
                            label={t('bexioPatSave')}
                            variant='primary'
                            icon='floppy'
                            onClick={savePat}
                          />
                        </Form>
                      </div>
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>

            {status?.connected && (
              <Row>
                <Col lg={4}>
                  <div className='d-flex justify-between section-title-blade'>
                    <div className='pe-4 sm:px-0'>
                      <p className='mt-1 text-draft'>{t('bexioImportContacts')}</p>
                    </div>
                  </div>
                </Col>
                <Col lg={8}>
                  <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                    <p className='text-muted mb-3'>{t('bexioImportDescription')}</p>

                    <KButton
                      id='btn-bexio-load'
                      label={loadingContacts ? t('bexioLoadingContacts') : t('bexioLoadContacts')}
                      variant='secondary'
                      icon='people'
                      disabled={loadingContacts}
                      onClick={loadContacts}
                    />

                    {contacts !== null && (
                      <>
                        {contacts.length === 0 ? (
                          <p className='text-muted mt-3'>{t('bexioNoContacts')}</p>
                        ) : (
                          <>
                            <div className='mt-3' style={{ maxHeight: 400, overflowY: 'auto' }}>
                              <Table hover size='sm' className='mb-0'>
                                <thead className='table-light'>
                                  <tr>
                                    <th style={{ width: 40 }}>
                                      <Form.Check checked={allSelected} onChange={toggleAll} />
                                    </th>
                                    <th>{t('bexioColType')}</th>
                                    <th>{t('bexioColName')}</th>
                                    <th>{t('bexioColFirstName')}</th>
                                    <th>{t('bexioColAddress')}</th>
                                    <th>{t('bexioColBirthday')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contacts.map(c => (
                                    <tr
                                      key={c.id}
                                      onClick={() => toggleContact(c.id)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <td>
                                        <Form.Check
                                          checked={selectedIds.has(c.id)}
                                          onChange={() => toggleContact(c.id)}
                                          onClick={e => e.stopPropagation()}
                                        />
                                      </td>
                                      <td>
                                        <span
                                          className={`badge ${c.contact_type_id === 2 ? 'bg-primary' : 'bg-secondary'}`}
                                        >
                                          {c.contact_type_id === 2
                                            ? t('bexioPerson')
                                            : t('bexioCompany')}
                                        </span>
                                      </td>
                                      <td>{c.name_1}</td>
                                      <td>{c.name_2 ?? '—'}</td>
                                      <td>{c.address ?? '—'}</td>
                                      <td>{c.birthday ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>

                            <div className='mt-3 d-flex align-items-center gap-3'>
                              <KButton
                                id='btn-bexio-import'
                                label={
                                  importing
                                    ? t('bexioImporting')
                                    : `${t('bexioImportSelected')} (${selectedIds.size})`
                                }
                                variant='primary'
                                icon='download'
                                disabled={importing || selectedIds.size === 0}
                                onClick={importSelected}
                              />
                              {importResult && (
                                <span className='text-success'>
                                  {t('bexioImportResult')}: {importResult.created}{' '}
                                  {t('bexioCreated')}, {importResult.updated} {t('bexioUpdated')}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </main>
      </Row>
    </Container>
  )
}
